import { useState } from 'react';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { profileApi } from '@/features/profile/api';
import { queryKeys } from '@/lib/query-keys';

interface MentionLinkProps {
  label: string;
  targetId: string;
  targetKind: 'user' | 'business';
}

/** Long enough that brushing past a name does not fire a request. */
const HOVER_DELAY_MS = 250;

/**
 * A tagged person inside a body of text.
 *
 * Shows the display name, not the handle — a mention should read like a name in
 * the middle of a sentence. The link travels by id rather than by name, because
 * a display name is not unique and cannot be a URL; the profile page swaps
 * itself to `/u/username` once it knows who it is looking at.
 */
export function MentionLink({ label, targetId, targetKind }: MentionLinkProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pending, setPending] = useState<number | null>(null);

  const href = targetKind === 'business' ? `/b/${targetId}` : `/u/${targetId}`;

  // Only fetched once someone lingers, and cached after that: a feed page can
  // hold a dozen mentions and none of them should cost a request on render.
  const profile = useQuery({
    queryKey: queryKeys.profile(targetId),
    queryFn: ({ signal }) => profileApi.get(targetId, signal),
    enabled: anchor !== null && targetKind === 'user',
    staleTime: 5 * 60_000,
  });

  const open = (event: React.MouseEvent<HTMLElement>): void => {
    const element = event.currentTarget;
    setPending(window.setTimeout(() => setAnchor(element), HOVER_DELAY_MS));
  };

  const close = (): void => {
    if (pending !== null) window.clearTimeout(pending);
    setPending(null);
    setAnchor(null);
  };

  return (
    <>
      <Link
        component={RouterLink}
        to={href}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={(event) => setAnchor(event.currentTarget)}
        onBlur={close}
        // The link sits inside someone else's paragraph; stopping the click
        // keeps it from also opening the card behind it.
        onClick={(event) => event.stopPropagation()}
        underline="hover"
        sx={{ color: 'mention', fontWeight: 500 }}
      >
        {label}
      </Link>

      <Popper
        open={anchor !== null}
        anchorEl={anchor}
        placement="top-start"
        style={{ zIndex: 1500 }}
      >
        <Paper
          variant="outlined"
          sx={{ p: 1.5, minWidth: 220, mb: 0.5 }}
          onMouseEnter={() => setAnchor(anchor)}
          onMouseLeave={close}
        >
          {profile.data ? (
            <Stack
              component={RouterLink}
              to={href}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ textDecoration: 'none', color: 'inherit' }}
              onClick={(event) => event.stopPropagation()}
            >
              <Avatar src={profile.data.avatarUrl ?? undefined} sx={{ width: 40, height: 40 }}>
                {profile.data.displayName.charAt(0)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {profile.data.displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  @{profile.data.username}
                </Typography>
                {profile.data.homeTown && (
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {profile.data.homeTown}
                  </Typography>
                )}
              </Box>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Skeleton variant="circular" width={40} height={40} />
              <Box>
                <Skeleton width={120} />
                <Skeleton width={80} />
              </Box>
            </Stack>
          )}
        </Paper>
      </Popper>
    </>
  );
}
